/**
 * Tenants API
 */

import { Tenant, TenantCreate } from '../types/api';
import { getAPIClient } from './client';

export async function listTenants(): Promise<Tenant[]> {
  const client = getAPIClient();
  return client.get<Tenant[]>('/tenants');
}

export async function getTenant(tenantId: string): Promise<Tenant> {
  const client = getAPIClient();
  return client.get<Tenant>(`/tenants/${tenantId}`);
}

export async function createTenant(data: TenantCreate): Promise<Tenant> {
  const client = getAPIClient();
  return client.post<Tenant>('/tenants', data);
}

export async function updateTenant(tenantId: string, data: Partial<TenantCreate>): Promise<Tenant> {
  const client = getAPIClient();
  return client.patch<Tenant>(`/tenants/${tenantId}`, data);
}

// ============================================================================
// EMAIL CONFIGURATION (Power Automate — theme from tenant ThemeDefinition)
// ============================================================================

export interface EmailConfig {
  enabled: boolean;
  fromEmail: string;
  fromName: string;
  baseUrl: string;
  powerAutomateConfigured: boolean;
  // Note: Theme colors come from tenant.configuration['theme'] or ['customization']['theme']
}

export interface EmailConfigUpdateRequest {
  enabled?: boolean;
  fromEmail?: string;
  fromName?: string;
  baseUrl?: string;
  powerAutomateUrl?: string;
  /** Leave blank to keep existing encrypted key */
  powerAutomateApiKey?: string;
}

export interface TestEmailRequest {
  toEmail: string;
}

export interface TestEmailResponse {
  success: boolean;
  message: string;
}

export async function getTenantEmailConfig(tenantId: string): Promise<EmailConfig> {
  const client = getAPIClient();
  const data = await client.get<any>(`/tenants/${tenantId}/email-config`);
  return {
    enabled: data.enabled,
    fromEmail: data.from_email,
    fromName: data.from_name,
    baseUrl: data.base_url,
    powerAutomateConfigured: data.power_automate_configured,
  };
}

export async function updateTenantEmailConfig(
  tenantId: string,
  data: EmailConfigUpdateRequest
): Promise<EmailConfig> {
  const client = getAPIClient();
  const requestData: Record<string, unknown> = {};
  if (data.enabled !== undefined) requestData.enabled = data.enabled;
  if (data.fromEmail !== undefined) requestData.from_email = data.fromEmail;
  if (data.fromName !== undefined) requestData.from_name = data.fromName;
  if (data.baseUrl !== undefined && data.baseUrl.trim()) requestData.base_url = data.baseUrl.trim();
  if (data.powerAutomateUrl !== undefined) {
    requestData.power_automate_url = data.powerAutomateUrl.trim() || null;
  }
  if (data.powerAutomateApiKey !== undefined && data.powerAutomateApiKey.trim()) {
    requestData.power_automate_api_key = data.powerAutomateApiKey;
  }
  const response = await client.put<any>(`/tenants/${tenantId}/email-config`, requestData);
  return {
    enabled: response.enabled,
    fromEmail: response.from_email,
    fromName: response.from_name,
    baseUrl: response.base_url,
    powerAutomateConfigured: response.power_automate_configured,
  };
}

export async function testTenantEmail(
  tenantId: string,
  request: TestEmailRequest
): Promise<TestEmailResponse> {
  const client = getAPIClient();
  return client.post<TestEmailResponse>(`/tenants/${tenantId}/email-config/test`, {
    to_email: request.toEmail,
  });
}
